import { rememberCompletionReturn } from "../../utils/profileCompletionReturn";
import { hasMeetingLocation } from "../../utils/meetingLocationSelection";
import MeetingLocationPicker from "../../components/meetingLocation/MeetingLocationPicker";
import { parseMapLocation } from "./mapLocation";
import { loadMapImage } from "./mapImageQueue";
import { isOldProfileWithoutDogImages } from "../../models/mapProfileVisibility";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setNearbyDogsCache } from "../../redux/userSlice";
import {
  GoogleMap,
  useLoadScript,
  OverlayViewF,
  MarkerF,
} from "@react-google-maps/api";
import {
  List,
  X,
  MapPin,
  Dot,
  ChevronDown,
  ArrowLeft,
  Loader2,
  Heart,
  Bone,
} from "lucide-react";
import { format } from "date-fns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { Box } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";

import "./MapDashboardPage.css";
import { getVisibleMapDogs } from "./visibleMapDogs";

import mapIcon from "../../assets/dashboardMapIcon.svg";
import pawPrintIcon from "../../assets/pawIcon.svg";
import calendarIcon from "../../assets/calendarIcon.svg";
import cake from "../../assets/cake.svg";
import pawIcon2 from "../../assets/pawIcon2_1.svg";
import pawIcon3 from "../../assets/pawIcon3.svg";
import tablerBone from "../../assets/tabler_bone.png";
import morningIcon from "../../assets/availability1.png";
import afternoonIcon from "../../assets/availability2.png";
import eveningIcon from "../../assets/availability3.png";
import weekedaysIcon from "../../assets/availability4.png";
import weekenedsIcon from "../../assets/availability5.png";
import vacationIcon from "../../assets/availability6.png";
import apiClient from "../../api/apiClient";
import { ENDPOINTS } from "../../api/endPoints";
import FilterPanel from "../../components/FilterPanel";
import { useGlobalAlert } from "../../context/AlertContext";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";
import appIcon from "../../assets/appIcon.svg";
import filterB from "../../assets/topNavBarIcons/filterB.svg";
import TopNavBar from "../../components/TopNavBar";
import PermissionEnforcer from "../../components/PermissionEnforcer";

import {
  collection,
  setDoc,
  addDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase-config";

const loadedImageCache = new Set();

const ImageWithPlaceholder = ({
  src,
  placeholder,
  alt,
  className,
  style,
  onLoadCallback,
}) => {
  const [isLoading, setIsLoading] = useState(() => {
    return src && src !== placeholder && !loadedImageCache.has(src);
  });
  const [imgSrc, setImgSrc] = useState(() => {
    if (!src || src === placeholder) return placeholder;
    return loadedImageCache.has(src) ? src : placeholder;
  });

  useEffect(() => {
    if (!src || src === placeholder) {
      setImgSrc(placeholder);
      setIsLoading(false);
      return;
    }

    if (loadedImageCache.has(src)) {
      setImgSrc(src);
      setIsLoading(false);
      if (onLoadCallback) onLoadCallback();
      return;
    }

    setImgSrc(placeholder);
    setIsLoading(true);
    return loadMapImage(src, (success) => {
      if (success) {
        loadedImageCache.add(src);
        // This cache holds URL strings only; bound its lifetime growth.
        if (loadedImageCache.size > 300) loadedImageCache.delete(loadedImageCache.values().next().value);
        setImgSrc(src);
        if (onLoadCallback) onLoadCallback();
      } else {
        setImgSrc(placeholder);
      }
      setIsLoading(false);
    });
  }, [src, placeholder, onLoadCallback]);

  return (
    <>
      <img
        decoding="async"
        src={imgSrc}
        alt={alt}
        className={className}
        style={{
          ...style,
          opacity: isLoading ? 0.5 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <Loader2 className="animate-spin" size={24} color="#005b7d" />
        </div>
      )}
    </>
  );
};

// CONSTANTS
const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "100%" };
const fallbackCenter = { lat: 40.7648, lng: -73.9808 }; // NYC Fallback
const mapOptions = {
  disableDefaultUI: true,
  zoomControl: false,
  scaleControl: true,
  gestureHandling: "greedy",
  styles: [
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [{ color: "#e5f5e0" }],
    },
    {
      featureType: "poi",
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "labels",
      stylers: [{ visibility: "on" }],
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
  ],
};

const getJitteredCoords = (lat, lng, index, total) => {
  if (total <= 1 || index === 0) return { lat, lng };
  const angle = (index * 2 * Math.PI) / (total - 1);
  const radius = 0.00018;
  return {
    lat: lat + radius * Math.cos(angle),
    lng: lng + radius * Math.sin(angle),
  };
};

const getAvatarData = (profileObj) => {
  if (!profileObj) return { src: null, placeholder: dogPlaceholder };

  const isTeamPaws =
    profileObj.user_dog_status === 1 ||
    profileObj.team_paws === 1 ||
    profileObj.dogs?.[0]?.team_paws === 1;

  const dogImg =
    profileObj.dog_images?.[0] || profileObj.dogs?.[0]?.dog_images?.[0];
  const userImg =
    profileObj.user_profile_image?.[0] ||
    profileObj.profile_image?.[0] ||
    profileObj.dogs?.[0]?.user_profile_image?.[0] ||
    profileObj.dogs?.[0]?.profile_image?.[0];

  if (isTeamPaws) {
    if (dogImg && dogImg.trim() !== "")
      return { src: dogImg, placeholder: dogPlaceholder };
    if (userImg && userImg.trim() !== "")
      return { src: userImg, placeholder: userPlaceholder };
    return { src: null, placeholder: dogPlaceholder };
  }

  if (userImg && userImg.trim() !== "")
    return { src: userImg, placeholder: userPlaceholder };
  return { src: null, placeholder: userPlaceholder };
};

const getDynamicDateLabel = (date) => {
  if (!date) return "FLEXIBLE";
  const target = dayjs(date);
  const today = dayjs();

  if (target.isSame(today, "day")) return "TODAY";
  if (target.isSame(today.add(1, "day"), "day")) return "TOMORROW";

  return "DATE";
};

const getDynamicTimeLabel = (date) => {
  if (!date) return "TIME";
  const hour = dayjs(date).hour();

  if (hour >= 0 && hour < 12) return "MORNING";
  if (hour >= 12 && hour < 17) return "AFTERNOON";

  return "EVENING";
};

const MapDashboardPage = ({
  onSwitchToList,
  filterData,
  setFilterData,
  filteredDogs,
  setFilteredDogs,
  isFilterActive,
  setIsFilterActive,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sharedUserId = new URLSearchParams(location.search).get("profileId");
  const sharedDogId = new URLSearchParams(location.search).get("dogId");
  const [sharedDog, setSharedDog] = useState(null);
  const openedShare = useRef(null);
  const dispatch = useDispatch();
  const { triggerAlert } = useGlobalAlert();

  const {
    profile,
    nearbyDogs: cachedDogs,
    loading,
  } = useSelector((state) => state.user);
  const hasFetchedInitialRef = useRef(false);

  const [isRestoredView] = useState(
    () => !!sessionStorage.getItem("map_dashboard_view"),
  );

  const getSavedView = () => {
    const saved = sessionStorage.getItem("map_dashboard_view");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { center: fallbackCenter, zoom: 12 };
      }
    }
    return { center: fallbackCenter, zoom: 12 };
  };

  const getSavedUserLocation = () => {
    const saved = localStorage.getItem("user_location_coords");
    if (saved) {
      try {
        const location = JSON.parse(saved);
        return parseMapLocation(location?.lat, location?.lng);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const [mapView, setMapView] = useState(getSavedView());
  const [mapRef, setMapRef] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [mobileMap, setMobileMap] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const onChange = () => setMobileMap(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const mapTouch = useRef(null);

  const handleMapTouchStart = (event) => {
    if (event.touches.length !== 1) {
      if (mapTouch.current) mapTouch.current.cancelled = true;
      return;
    }
    const touch = event.touches[0];
    mapTouch.current = { x: touch.clientX, y: touch.clientY, cancelled: false };
  };

  const handleMapTouchMove = (event) => {
    const gesture = mapTouch.current;
    if (!gesture) return;
    const touch = event.touches[0];
    if (event.touches.length !== 1 ||
        Math.hypot(touch.clientX - gesture.x, touch.clientY - gesture.y) > 8) {
      gesture.cancelled = true;
    }
  };

  const [hasRecentered, setHasRecentered] = useState(false);
  const [userLocation, setUserLocation] = useState(getSavedUserLocation());
  const [profileSearchLocation, setProfileSearchLocation] = useState(null);
  const searchLocation = userLocation || profileSearchLocation;
  const searchLocationKey = searchLocation ? `${Number(searchLocation.lat).toFixed(4)},${Number(searchLocation.lng).toFixed(4)}` : "";
  const [profileLocationNotice, setProfileLocationNotice] = useState("");
  useEffect(() => {
    if (!profileLocationNotice) return;
    const timeout = setTimeout(() => setProfileLocationNotice(""), 10_000);
    return () => clearTimeout(timeout);
  }, [profileLocationNotice]);
  const nearbyRequestRef = useRef(0);
  const [nearbyDogs, setNearbyDogs] = useState(cachedDogs || []);

  const lastFetchedLocRef = useRef(getSavedUserLocation());

  const handleReCenter = () => {
    if (!userLocation) return;
    if (mapRef && typeof mapRef.panTo === "function") {
      mapRef.panTo(userLocation);
      mapRef.setZoom(14);
      const newView = { center: userLocation, zoom: 14 };
      setMapView(newView);
      sessionStorage.setItem("map_dashboard_view", JSON.stringify(newView));
    }
  };

  const onMapLoad = useCallback((map) => {
    setMapRef(map);
  }, []);

  const onMapIdle = () => {
    if (mapRef && typeof mapRef.getCenter === "function") {
      try {
        const centerObj = mapRef.getCenter();
        if (centerObj) {
          const currentCenter = centerObj.toJSON();
          const currentZoom = mapRef.getZoom();
          const newView = { center: currentCenter, zoom: currentZoom };
          sessionStorage.setItem("map_dashboard_view", JSON.stringify(newView));
        }

        if (typeof mapRef.getBounds === "function") {
          const b = mapRef.getBounds();
          if (b) {
            const ne = b.getNorthEast();
            const sw = b.getSouthWest();
            const nextBounds = {
              n: ne.lat(),
              e: ne.lng(),
              s: sw.lat(),
              w: sw.lng(),
            };
            setBounds((previous) =>
              previous && Object.keys(nextBounds).every(
                (key) => previous[key] === nextBounds[key],
              ) ? previous : nextBounds,
            );
          }
        }
      } catch (error) {
        console.error("Error saving map view:", error);
      }
    }
  };

  // STATES
  const [selectedDogId, setSelectedDogId] = useState(null);
  const [treatDog, setTreatDog] = useState(null);
  const [showTreatModal, setShowTreatModal] = useState(false);
  const [showBookWalkModal, setShowBookWalkModal] = useState(false);
  const [walkTab, setWalkTab] = useState("calendar");
  const [selectedFlexibleOptions, setSelectedFlexibleOptions] = useState([]);
  const [showWalkConfirmModal, setShowWalkConfirmModal] = useState(false);
  const [showMeetingPointModal, setShowMeetingPointModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [walkMessage, setWalkMessage] = useState("");
  const [isTossingTreat, setIsTossingTreat] = useState(false);
  const [isCreatingWalk, setIsCreatingWalk] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [value, setValue] = useState(dayjs());
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [meetingAddress, setMeetingAddress] = useState("");
  const [selectedMeetingLocation, setSelectedMeetingLocation] = useState(null);

  const getFirstName = (fullName) => {
    if (!fullName) return "";
    return fullName.trim().split(" ")[0];
  };

  useEffect(() => {
    const savedIsFilterActive = sessionStorage.getItem(
      "social_paws_isFilterActive",
    );
    if (savedIsFilterActive === "true" && !isFilterActive) {
      try {
        const savedFilteredDogs = JSON.parse(
          sessionStorage.getItem("social_paws_filteredDogs"),
        );
        const savedFilterData = JSON.parse(
          sessionStorage.getItem("social_paws_filterData"),
        );

        if (savedFilteredDogs) setFilteredDogs(savedFilteredDogs);
        if (savedFilterData) setFilterData(savedFilterData);
        setIsFilterActive(true);
      } catch (e) {
        console.error("Failed to parse saved filters", e);
      }
    }
  }, []);

  const profileId = profile?.id;
  const isLoggedPupParent =
    profile?.team_paws === 1 || profile?.user_dog_status === 1;

  const isLoggedPupPal =
    (profile?.team_social === 1 && profile?.team_paws !== 1) ||
    profile?.user_dog_status === 0;

  const fetchNearbyDogs = useCallback(
    async (lat, lng, force = false) => {
      if (!profileId) return;
      const requestId = ++nearbyRequestRef.current;

      if (cachedDogs && cachedDogs.length > 0) {
        setNearbyDogs(cachedDogs);
        if (!force) return;
      }

      try {
        const payload = { latitude: lat, longitude: lng };
        const response = await apiClient.post(
          ENDPOINTS.GET_NEARBY_DOGS,
          payload,
        );

        if (requestId !== nearbyRequestRef.current) return;
        if (response.data?.status) {
          let fetchedDogs = response.data.dogs || [];

          fetchedDogs = fetchedDogs.filter((dog) => {
            if (isOldProfileWithoutDogImages(dog)) return false;
            if (dog.is_old_user === 1 || dog.is_old_user === true) {
              return (
                dog.isDogProfileComplete === 1 ||
                dog.isDogProfileComplete === true ||
                dog.isDogProfileHalfComplete === 1 ||
                dog.isDogProfileHalfComplete === true
              );
            }
            return true;
          });

          if (!isLoggedPupParent && isLoggedPupPal) {
            fetchedDogs = fetchedDogs.filter(
              (dog) => dog.team_paws === 1 || dog.user_dog_status === 1,
            );
          }

          setNearbyDogs(fetchedDogs);
          dispatch(setNearbyDogsCache(fetchedDogs));
        }
      } catch (error) {
        console.error("Error fetching nearby dogs:", error);
      }
    },
    [profileId, isLoggedPupParent, isLoggedPupPal, dispatch, cachedDogs],
  );

  const fetchRef = useRef(fetchNearbyDogs);
  useEffect(() => {
    fetchRef.current = fetchNearbyDogs;
  }, [fetchNearbyDogs]);

  // GEOLOCATION LISTENER
  useEffect(() => {
    const savedLoc = getSavedUserLocation();
    hasFetchedInitialRef.current = false;
    let active = true;
    let hasCurrentLocation = Boolean(savedLoc);
    let fallbackRequested = false;
    let usedProfileLocation = false;
    const controller = new AbortController();

    const loadProfileLocationFallback = async () => {
      if (!active || hasCurrentLocation || fallbackRequested || !profileId) return;
      fallbackRequested = true;
      try {
        const response = await apiClient.get(ENDPOINTS.GET_USER_DETAILS(profileId), {
          signal: controller.signal,
        });
        if (!active || hasCurrentLocation) return;
        const details = response.data?.status ? response.data.data : null;
        const position = parseMapLocation(details?.user_latitude, details?.user_longitude);
        if (!position) {
          setProfileLocationNotice("Your profile has no saved map location. Please update your profile address or enable location access.");
          return;
        }
        usedProfileLocation = true;
        setProfileSearchLocation(position);
        setProfileLocationNotice("Showing nearby profiles around your saved profile location.");
        setMapView({ center: position, zoom: 14 });
        fetchRef.current(position.lat, position.lng, true);
      } catch (error) {
        if (active && !hasCurrentLocation) {
          setProfileLocationNotice("Could not load your saved profile location. Please try again or enable location access.");
        }
      }
    };
    const cleanup = () => {
      active = false;
      controller.abort();
      nearbyRequestRef.current += 1;
    };

    if (savedLoc && profileId && !hasFetchedInitialRef.current) {
      hasFetchedInitialRef.current = true;
      lastFetchedLocRef.current = savedLoc;
      fetchRef.current(savedLoc.lat, savedLoc.lng, true);
    }

    if (!navigator.geolocation) {
      loadProfileLocationFallback();
      return cleanup;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!active) return;
        hasCurrentLocation = true;
        setProfileLocationNotice("");
        window.dispatchEvent(new Event("locationGranted"));
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        lastFetchedLocRef.current = pos;
        setUserLocation(pos);
        localStorage.setItem("user_location_coords", JSON.stringify(pos));

        if (usedProfileLocation || !sessionStorage.getItem("map_dashboard_view")) {
          setMapView({ center: pos, zoom: 14 });
        }

        if (usedProfileLocation || !hasFetchedInitialRef.current) {
          usedProfileLocation = false;
          hasFetchedInitialRef.current = true;
          fetchRef.current(pos.lat, pos.lng, true);
        }
      },
      (error) => {
        console.error("Geolocation Error:", error.message);
        loadProfileLocationFallback();
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 },
    );

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!active) return;
        hasCurrentLocation = true;
        setProfileLocationNotice("");
        window.dispatchEvent(new Event("locationGranted"));
        if (usedProfileLocation) {
          setMapView({ center: { lat: position.coords.latitude, lng: position.coords.longitude }, zoom: 14 });
          usedProfileLocation = false;
        }
        const newLat = position.coords.latitude;
        const newLng = position.coords.longitude;
        const lastLoc = lastFetchedLocRef.current;

        if (
          lastLoc &&
          Math.abs(lastLoc.lat - newLat) < 0.01 &&
          Math.abs(lastLoc.lng - newLng) < 0.01
        ) {
          return;
        }

        const pos = { lat: newLat, lng: newLng };
        lastFetchedLocRef.current = pos;

        setUserLocation(pos);
        localStorage.setItem("user_location_coords", JSON.stringify(pos));
        fetchRef.current(pos.lat, pos.lng, true);
      },
      (error) => {
        console.error("Watch location error:", error);
        loadProfileLocationFallback();
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 },
    );

    return () => {
      cleanup();
      navigator.geolocation.clearWatch(watchId);
    };
  }, [profileId]);

  useEffect(() => {
    if (mapRef && userLocation && !hasRecentered) {
      if (!isRestoredView) {
        mapRef.panTo(userLocation);
        mapRef.setZoom(14);
        const initialView = { center: userLocation, zoom: 14 };
        setMapView(initialView);
        sessionStorage.setItem(
          "map_dashboard_view",
          JSON.stringify(initialView),
        );
      }
      setHasRecentered(true);
    }
  }, [mapRef, userLocation, hasRecentered, isRestoredView]);

  const selectedDog = nearbyDogs.find((d) => d.id === selectedDogId) ||
    (sharedDog?.id === selectedDogId ? sharedDog : null);

  // Time Slots Generation
  const TIME_SLOTS = useMemo(() => {
    const interval = 30;
    const slots = [];
    const now = dayjs();
    const isToday = value && value.isSame(now, "day");

    const currentMinute = now.minute();
    const addMinutes = interval - (currentMinute % interval);
    const nextAvailableTime = now
      .clone()
      .add(addMinutes, "minute")
      .second(0)
      .millisecond(0);

    for (let h = 6; h <= 21; h++) {
      for (let m = 0; m < 60; m += interval) {
        if (h === 21 && m > 0) break;

        const slotTime = (value || now)
          .clone()
          .hour(h)
          .minute(m)
          .second(0)
          .millisecond(0);

        if (isToday) {
          if (
            slotTime.isSame(nextAvailableTime) ||
            slotTime.isAfter(nextAvailableTime)
          ) {
            slots.push(slotTime.format("hh:mm A"));
          }
        } else {
          slots.push(slotTime.format("hh:mm A"));
        }
      }
    }

    return slots;
  }, [value]);

  const handlePinClick = (dog) => {
    setSelectedDogId((prev) => {
      if (prev === dog.id) {
        return prev;
      }

      if (mapRef && dog.displayCoords) {
        const projection = mapRef.getProjection();

        if (projection && typeof mapRef.getZoom === "function") {
          const scale = Math.pow(2, mapRef.getZoom());

          const centerLatLng = new window.google.maps.LatLng(
            dog.displayCoords.lat,
            dog.displayCoords.lng,
          );
          const worldCoord = projection.fromLatLngToPoint(centerLatLng);

          const pixelOffset = -120;
          const newWorldCoord = new window.google.maps.Point(
            worldCoord.x,
            worldCoord.y + pixelOffset / scale,
          );

          const newCenter = projection.fromPointToLatLng(newWorldCoord);
          mapRef.panTo(newCenter);
        } else {
          mapRef.panTo(dog.displayCoords);
        }
      }

      return dog.id;
    });
  };

  const closeCard = () => {
    setSelectedDogId(null);
  };

  const [showIncompleteProfileModal, setShowIncompleteProfileModal] =
    useState(false);

  const checkCompletionStatus = () => {
    const isComplete = profile?.dogs?.[0]?.isDogProfileComplete;
    const isHalfComplete = profile?.dogs?.[0]?.isDogProfileHalfComplete;

    if (isComplete === 1) {
      return true;
    }

    if (isComplete === 0 && isHalfComplete === 1) {
      rememberCompletionReturn(profile?.id, { dogData: selectedDog });
      navigate("/half-completed-page");
      return false;
    }

    setShowIncompleteProfileModal(true);
    return false;
  };

  const handleTossTreat = async (dog) => {
    if (!checkCompletionStatus()) return;

    if (!profile?.id || !dog?.user_id) {
      triggerAlert("User or Dog information missing", "error");
      return;
    }

    setIsTossingTreat(true);

    try {
      const payload = {
        sender_user_id: profile?.id,
        receiver_user_id: dog?.user_id,
        receiver_dog_id: dog?.id,
      };

      const response = await apiClient.post(
        ENDPOINTS.CREATE_WEBAPP_TREAT,
        payload,
      );

      if (response.data?.status) {
        setTreatDog(dog);
        setShowTreatModal(true);
        setSelectedDogId(null);
      } else {
        triggerAlert(
          response.data?.message || "Failed to toss a treat.",
          "error",
        );
      }
    } catch (error) {
      console.error("Error tossing treat:", error);

      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          `Server Error: ${error.response.status}`;
        triggerAlert(errorMessage, "error");
      } else if (error.request) {
        triggerAlert(
          "No response from server. Please check your internet connection.",
          "error",
        );
      } else {
        triggerAlert("An unexpected error occurred.", "error");
      }
    } finally {
      setIsTossingTreat(false);
    }
  };

  const displayDogs = useMemo(() => {
    let dogsToDisplay = isFilterActive ? filteredDogs : nearbyDogs;

    dogsToDisplay = dogsToDisplay.filter((dog) => {
      if (isOldProfileWithoutDogImages(dog)) return false;

      const lat = dog.user_latitude;
      const lng = dog.user_longitude;

      if (lat == null || lat === "" || lng == null || lng === "") return false;

      const numLat = parseFloat(lat);
      const numLng = parseFloat(lng);

      if (isNaN(numLat) || numLat === 0 || isNaN(numLng) || numLng === 0) {
        return false;
      }

      return true;
    });

    if (!isLoggedPupParent && isLoggedPupPal) {
      dogsToDisplay = dogsToDisplay.filter(
        (dog) => dog.team_paws === 1 || dog.user_dog_status === 1,
      );
    }

    if (sharedDog && !dogsToDisplay.some((dog) => String(dog.id) === String(sharedDog.id))) {
      dogsToDisplay = [...dogsToDisplay, sharedDog];
    }
    return dogsToDisplay.filter((dog) => !isOldProfileWithoutDogImages(dog));
  }, [
    sharedDog,
    isFilterActive,
    filteredDogs,
    nearbyDogs,
    isLoggedPupParent,
    isLoggedPupPal,
  ]);

  const displayDogsWithJitter = useMemo(() => {
    const coordCounts = {};
    const coordTrackers = {};

    displayDogs.forEach((dog) => {
      const lat = parseFloat(dog.user_latitude).toFixed(5);
      const lng = parseFloat(dog.user_longitude).toFixed(5);
      const key = `${lat}_${lng}`;
      coordCounts[key] = (coordCounts[key] || 0) + 1;
    });

    return displayDogs.map((dog) => {
      const lat = parseFloat(dog.user_latitude).toFixed(5);
      const lng = parseFloat(dog.user_longitude).toFixed(5);
      const key = `${lat}_${lng}`;

      const currentIdx = coordTrackers[key] || 0;
      coordTrackers[key] = currentIdx + 1;

      const modifiedCoords = getJitteredCoords(
        parseFloat(dog.user_latitude),
        parseFloat(dog.user_longitude),
        currentIdx,
        coordCounts[key],
      );

      return {
        ...dog,
        displayCoords: modifiedCoords,
      };
    });
  }, [displayDogs]);

  // Resolve shared links using the recipient's location, never the sender's.
  useEffect(() => {
    setSharedDog(null);
    openedShare.current = null;
    if (!sharedUserId || !profileId) return;
    let cancelled = false;
    const loadSharedProfile = async () => {
      try {
        const response = userLocation ? await apiClient.post(ENDPOINTS.GET_NEARBY_DOGS, {
          latitude: userLocation.lat,
          longitude: userLocation.lng,
        }).catch(() => null) : null;
        if (cancelled) return;
        let match = response?.data?.status && response.data.dogs?.find((dog) =>
          String(dog.user_id) === sharedUserId && (!sharedDogId || String(dog.id) === sharedDogId),
        );
        if (!match) {
          const details = await apiClient.get(`/user/details/${encodeURIComponent(sharedUserId)}`);
          if (cancelled) return;
          const user = details.data?.status && details.data.data;
          if (!user) throw new Error("Profile unavailable");
          const dog = sharedDogId
            ? user.dogs?.find((item) => String(item.id) === sharedDogId)
            : user.dogs?.[0];
          if (sharedDogId && !dog && String(user.id) !== sharedDogId) {
            throw new Error("Profile unavailable");
          }
          match = {
            ...user,
            ...dog,
            id: dog?.id ?? user.id,
            user_id: user.id,
            user_latitude: user.user_latitude,
            user_longitude: user.user_longitude,
            profile_image: user.user_profile_image || user.profile_image || [],
            team_social: Number(user.user_dog_status) === 1 ? 0 : 1,
          };
        }
        if (match.user_latitude != null && match.user_longitude != null &&
            match.user_latitude !== "" && match.user_longitude !== "" &&
            Number.isFinite(Number(match.user_latitude)) && Number.isFinite(Number(match.user_longitude))) {
          setSharedDog(match);
        } else {
          navigate("/profile-details", { state: { dogData: match } });
        }
      } catch {
        if (!cancelled) triggerAlert("Unable to load this shared profile. It may no longer be available.", "error");
      }
    };
    loadSharedProfile();
    return () => { cancelled = true; };
  }, [sharedUserId, sharedDogId, profileId, userLocation, triggerAlert, navigate]);

  useEffect(() => {
    if (!sharedDog || !mapRef || (userLocation && !hasRecentered) || openedShare.current === sharedDog) return;
    const dog = displayDogsWithJitter.find((item) => String(item.id) === String(sharedDog.id));
    if (!dog) return;
    openedShare.current = sharedDog;
    setHasRecentered(true);
    setMapView({ center: dog.displayCoords, zoom: 14 });
    mapRef.setZoom(14);
    mapRef.panTo(dog.displayCoords);
    mapRef.panBy(0, -120);
    setSelectedDogId(dog.id);
  }, [sharedDog, mapRef, hasRecentered, displayDogsWithJitter, userLocation]);

  const handleShareProfile = async (event, dog, copyOnly = false) => {
    event.stopPropagation();
    if (!dog.user_id) {
      triggerAlert("This profile cannot be shared yet.", "error");
      return;
    }
    const url = new URL("/profile-details", "https://webapp.socialpawsapp.com");
    url.searchParams.set("profileId", dog.user_id);
    if (dog.id != null) url.searchParams.set("dogId", dog.id);
    try {
      if (!copyOnly && navigator.share) {
        await navigator.share({ title: "Social Paws profile", text: "Meet this pack member on Social Paws!", url: url.href });
      } else {
        await navigator.clipboard.writeText(url.href);
        triggerAlert("Profile link copied! Paste it into WhatsApp to share.", "success");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        window.prompt("Copy this profile link to share:", url.href);
      }
    }
  };

  const visibleDogs = useMemo(
    () => getVisibleMapDogs(displayDogsWithJitter, bounds, selectedDogId, mobileMap ? 40 : 150),
    [displayDogsWithJitter, bounds, selectedDogId, mobileMap],
  );

  useEffect(() => {
    if (!searchLocationKey || !isFilterActive) return;
    if (sessionStorage.getItem("social_paws_filterLocation") !== searchLocationKey) {
      setIsFilterActive(false);
      setFilteredDogs([]);
      sessionStorage.removeItem("social_paws_isFilterActive");
      sessionStorage.removeItem("social_paws_filteredDogs");
      sessionStorage.removeItem("social_paws_filterLocation");
    }
  }, [searchLocationKey, isFilterActive, setIsFilterActive, setFilteredDogs]);

  const handleApplyFilters = (newFilteredList, { search = "" } = {}) => {
    // The field accepts both names and addresses. Focus returned profiles rather
    // than geocoding a name as an unrelated place.
    if (search && mapRef && window.google?.maps) {
      const positions = newFilteredList
        .map((dog) => parseMapLocation(dog.user_latitude, dog.user_longitude))
        .filter(Boolean);
      if (positions.length) {
        closeCard();
        // Use the first matching profile with valid coordinates for every search.
        mapRef.setCenter(positions[0]);
        mapRef.setZoom(16);
      }
    }
    sessionStorage.setItem("social_paws_filterLocation", searchLocationKey);
    setIsFilterActive(true);
    setFilteredDogs(newFilteredList);

    sessionStorage.setItem("social_paws_isFilterActive", "true");
    sessionStorage.setItem(
      "social_paws_filteredDogs",
      JSON.stringify(newFilteredList),
    );
    sessionStorage.setItem(
      "social_paws_filterData",
      JSON.stringify(filterData),
    );
  };

  const handleClearFilters = () => {
    setIsFilterActive(false);
    setFilteredDogs([]);
    setIsFilterOpen(false);
    setFilterData({
      teamSelection: "both",
      proximityValue: 5,
      availability: [""],
      breed: "",
      experience: [""],
      age: "",
    });

    sessionStorage.removeItem("social_paws_isFilterActive");
    sessionStorage.removeItem("social_paws_filteredDogs");
    sessionStorage.removeItem("social_paws_filterData");
  };

  const handleBookWalk = () => {
    if (!checkCompletionStatus()) return;

    setTreatDog(selectedDog);
    setShowBookWalkModal(true);
    setSelectedDogId(null);
  };

  const handleFlexibleSubmit = () => {
    if (selectedFlexibleOptions.length === 0) return;
    setSelectedDate(null);
    setSelectedTime(null);
    openMeetingPoint();
  };

  const openMeetingPoint = () => {
    setShowBookWalkModal(false);
    setShowMeetingPointModal(true);
  };

  const backToBookWalk = () => {
    setShowMeetingPointModal(false);
    setShowBookWalkModal(true);
  };

  const openWalkConfirm = () => {
    if (!hasMeetingLocation(meetingAddress)) {
      triggerAlert("Please select a meeting location.", "error");
      return;
    }
    setShowMeetingPointModal(false);
    setShowWalkConfirmModal(true);
  };

  const backToMeetingPoint = () => {
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(true);
  };

  const closeAllModalsFully = () => {
    setShowTreatModal(false);
    setShowBookWalkModal(false);
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(false);
    setShowSuccessModal(false);
    setTreatDog(null);
    setMeetingAddress("");
    setSelectedMeetingLocation(null);
    setValue(dayjs());
    setSelectedDate(null);
    setSelectedTime(null);
    setWalkMessage("");
  };

  const closeAllModals = () => {
    setShowTreatModal(false);
    setShowBookWalkModal(false);
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(false);
    setShowSuccessModal(false);
    setTreatDog(null);
    setValue(dayjs());
    setSelectedDate(null);
    setSelectedTime(null);
    setWalkMessage("");
  };

  const handleCloseModal = () => {
    setShowTreatModal(false);
    setShowBookWalkModal(false);
    setTreatDog(null);
    setValue(dayjs());
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const toggleFlexibleOption = (option) => {
    if (selectedFlexibleOptions.includes(option)) {
      setSelectedFlexibleOptions((prev) => prev.filter((o) => o !== option));
    } else {
      setSelectedFlexibleOptions((prev) => [...prev, option]);
    }
  };

  const handleCreateWalk = async () => {
    if (!profile?.id || !treatDog) {
      triggerAlert("Missing user or dog profile information.", "error");
      return;
    }

    if (!hasMeetingLocation(meetingAddress)) {
      triggerAlert("Please select a meeting location.", "error");
      return;
    }

    setIsCreatingWalk(true);

    try {
      const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const isTreatDogPupPal =
        (treatDog.team_social === 1 && treatDog.team_paws !== 1) ||
        treatDog.user_dog_status === 0;

      const effectiveDogId = isTreatDogPupPal
        ? profile?.dogs?.[0]?.id
        : treatDog.id;

      const payload = {
        requester_user_id: profile.id,
        walker_user_id: treatDog.user_id,
        dog_id: effectiveDogId,
        date: selectedDate ? selectedDate.toISOString() : null,
        availability: walkTab === "flexible" ? selectedFlexibleOptions : [],
        meeting_location: meetingAddress.trim(),
        timezone: deviceTimezone,
        sender_offer_message: walkMessage,
        receiver_offer_message: "",
      };

      const response = await apiClient.post(ENDPOINTS.CREATE_A_WALK, payload);

      if (response.data?.status) {
        setShowWalkConfirmModal(false);
        setShowSuccessModal(true);
      } else {
        triggerAlert(
          response.data?.message || "Failed to create walk request.",
          "error",
        );
      }
    } catch (error) {
      console.error("Create Walk Error:", error);
      triggerAlert("Something went wrong. Please try again.", "error");
    } finally {
      setIsCreatingWalk(false);
    }
  };

  const handleOnPress = (dog) => {
    navigate("/profile-details", {
      state: { dogData: dog },
    });
  };

  const toTitleCase = (value) => {
    if (!value || (typeof value === "string" && value.trim() === "")) {
      return "Not Specified";
    }

    if (typeof value !== "string") return value;

    return value
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getDogAgeCategory = (birthday) => {
    if (!birthday) return "Not Specified";

    const birthDate = new Date(birthday);
    const today = new Date();

    const diffTime = today - birthDate;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    const years = Math.floor(diffDays / 365.25);
    const months = Math.floor(diffDays / 30.44);

    if (years >= 1) {
      return `${years} ${years === 1 ? "Year" : "Years"} Old`;
    }

    return `${months} ${months === 1 ? "Month" : "Months"} Old`;
  };

  const joinIfArray = (value) => {
    if (!value) return "Not Specified";

    if (Array.isArray(value)) {
      return value.length ? value.join(", ") : "Not Specified";
    }

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.length ? parsed.join(", ") : "Not Specified";
        }
        return value.trim() === "" ? "Not Specified" : value;
      } catch {
        return value.trim() === "" ? "Not Specified" : value;
      }
    }

    return value;
  };

  const formatDistance = (miles = 0) => {
    const blocks = Math.round(miles * 20);

    if (blocks <= 20) {
      return `${blocks} ${blocks === 1 ? "block" : "blocks"} away`;
    }

    const roundedMiles = Number(miles.toFixed(1));
    return `${roundedMiles} ${roundedMiles === 1 ? "mile" : "miles"} away`;
  };

  if (loadError)
    return <div className="map-loading-error">Error loading maps</div>;

  if (!profile) {
    return <div>No user data found. Please log in.</div>;
  }

  if (!isLoaded || loading)
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(139.27deg, #009fd9 23.13%, #005b7d 98.96%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} color="#fff" />
      </div>
    );

  return (
    <div className="map-container">
      <PermissionEnforcer />
      {profileLocationNotice && (
        <div role="status" style={{ position: "absolute", top: "140px", left: "16px", right: "16px", zIndex: 10, padding: "10px 14px", background: "#e8fffa", borderRadius: "8px", color: "#173e36" }}>
          {profileLocationNotice}
        </div>
      )}

      {/* HEADER */}
      <div className="map-header map-header-desktop">
        <div className="map-header-logo-group">
          <img src={appIcon} alt="Social Paws" className="map-app-logo" />
          <span className="map-app-title">Social Paws</span>
          <span
            style={{
              color: "#fff",
              fontSize: "18px",
              marginLeft: "1rem",
              fontWeight: "700",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate("/help")}
          >
            Need Help?
          </span>
        </div>
        <TopNavBar theme="dark" />
      </div>

      <div className="map-header-mobile">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <span
            style={{
              color: "#fff",
              fontSize: "1rem",
              cursor: "pointer",
              textDecoration: "underline",
              fontWeight: "700",
              marginBottom: "12px",
            }}
            onClick={() => navigate("/help")}
          >
            Need Help?
          </span>
          <div className="map-header-logo-group-mobile">
            <img
              src={appIcon}
              alt="Social Paws"
              className="map-app-logo-mobile"
            />
            <span className="map-app-title-mobile">Social Paws</span>
          </div>
        </div>
        <TopNavBar theme="dark" />
      </div>

      <div className="card-divider-main" />

      {/* MAP AREA */}
      <div
        className="map-area"
        onTouchStartCapture={handleMapTouchStart}
        onTouchMoveCapture={handleMapTouchMove}
        onTouchCancelCapture={() => {
          if (mapTouch.current) mapTouch.current.cancelled = true;
        }}
        onPointerDownCapture={(event) => {
          if (event.pointerType === "mouse") mapTouch.current = null;
        }}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={mapView.zoom}
          center={mapView.center}
          onLoad={onMapLoad}
          onIdle={onMapIdle}
          options={mapOptions}
          onClick={closeCard}
        >
          {/* USER PIN */}
          {userLocation && (
            <OverlayViewF
              position={userLocation}
              mapPaneName="overlayMouseTarget"
            >
              <div
                className="user-location-pin"
                style={{ maxWidth: "none", minWidth: "100px" }}
              >
                <div className="pulse-ring" />
                <MapPin
                  size={24}
                  color="rgba(0, 155, 206, 1)"
                  fill="white"
                  className="mobile-map-pin"
                />
              </div>
            </OverlayViewF>
          )}

          {/* DOG PINS & CARDS */}
          {visibleDogs.map((dog) => {
            const isPupPalOnly =
              (dog.team_social === 1 && dog.team_paws !== 1) ||
              dog.user_dog_status === 0;
            const dogActivityDisplay = toTitleCase(
              dog.dog_activity?.toLowerCase() === "both"
                ? "Active companion"
                : dog.dog_activity?.toLowerCase() === "exercise"
                  ? "Exercise"
                  : dog.dog_activity?.toLowerCase() === "companionship"
                    ? "Companionship"
                    : "Not Specified",
            );

            const availabilityDisplay = toTitleCase(
              joinIfArray(dog.availability) !== "Not Specified"
                ? `${joinIfArray(dog.availability)}`
                : "Not Specified",
            );

            const isSelected = selectedDogId === dog.id;

            const handlePinAction = (e) => {
              e.stopPropagation();
              if (mapTouch.current?.cancelled) return;

              if (isSelected && String(dog.user_id) !== String(profile?.id)) {
                handleOnPress(dog);
              } else {
                handlePinClick(dog);
              }
            };

            const handleCardAction = (e) => {
              e.stopPropagation();
              if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
              if (e.type === "touchend") e.preventDefault();

              if (String(dog.user_id) !== String(profile?.id)) {
                handleOnPress(dog);
              }
            };

            const handleTossAction = (e) => {
              e.stopPropagation();
              if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
              if (e.type === "touchend") e.preventDefault();

              if (!isTossingTreat) {
                handleTossTreat(dog);
              }
            };

            const handleViewProfileAction = (e) => {
              e.stopPropagation();
              if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
              if (e.type === "touchend") e.preventDefault();
              handleOnPress(dog);
            };

            return (
              <OverlayViewF
                key={dog.id}
                position={dog.displayCoords}
                mapPaneName="overlayMouseTarget"
              >
                <div
                  style={{ position: "relative", zIndex: isSelected ? 100 : 1 }}
                >
                  <div
                    className={`dog-pin ${isSelected ? "pin-active" : ""}`}
                    style={{
                      transform: isSelected
                        ? "translate(-50%, -100%) scale(1.35)"
                        : "translate(-50%, -100%) scale(1)",
                      transition: "transform 0.25s ease-in-out",
                      cursor: "pointer",
                      boxShadow: isSelected
                        ? "0px 8px 16px rgba(0,0,0,0.3)"
                        : "none",
                      maxWidth: "none",
                      minWidth: "50px",
                    }}
                    onClick={handlePinAction}
                  >
                    <ImageWithPlaceholder
                      src={getAvatarData(dog).src}
                      placeholder={getAvatarData(dog).placeholder}
                      alt={dog?.dog_name}
                      style={{
                        maxWidth: "none",
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  </div>

                  {/* INFO CARD */}
                  {isSelected && (
                    <div
                      className="dog-info-card top"
                      style={{
                        bottom: "75px",
                        top: "auto",
                        maxWidth: "none",
                        minWidth: "280px",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (e.nativeEvent)
                          e.nativeEvent.stopImmediatePropagation();
                      }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        if (e.nativeEvent)
                          e.nativeEvent.stopImmediatePropagation();
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        if (e.nativeEvent)
                          e.nativeEvent.stopImmediatePropagation();
                      }}
                    >
                      <div
                        className="card-main-row"
                        onClick={handleCardAction}
                        onTouchEnd={handleCardAction}
                        onTouchStart={(e) => e.stopPropagation()}
                        style={{
                          cursor:
                            String(dog.user_id) !== String(profile?.id)
                              ? "pointer"
                              : "default",
                        }}
                      >
                        <div
                          className="map-info-card-dog-avatar"
                          style={{ position: "relative" }}
                        >
                          <ImageWithPlaceholder
                            src={getAvatarData(dog).src}
                            placeholder={getAvatarData(dog).placeholder}
                            alt={dog.dog_name}
                            style={{
                              maxWidth: "none",
                              width: "100%",
                              height: "100%",
                            }}
                          />
                        </div>

                        <div className="card-right-column">
                          <h2
                            className={`card-dog-name ${
                              (isPupPalOnly ? dog.user_name : dog.dog_name)
                                ?.length > 12
                                ? "scaled-name"
                                : ""
                            }`}
                          >
                            {isPupPalOnly ? (
                              <>
                                <Heart
                                  size={22}
                                  color="#000"
                                  strokeWidth={2.5}
                                  style={{ flexShrink: 0 }}
                                />
                                <span
                                  className="dog-name-text"
                                  title={dog.user_name}
                                >
                                  {dog.user_name}
                                </span>
                              </>
                            ) : (
                              <>
                                <Bone
                                  size={24}
                                  color="#000"
                                  strokeWidth={2}
                                  style={{
                                    transform: "rotate(45deg)",
                                    flexShrink: 0,
                                  }}
                                />
                                <span
                                  className="dog-name-text"
                                  title={dog.dog_name}
                                >
                                  {dog.dog_name}
                                </span>
                              </>
                            )}
                          </h2>

                          <div className="card-distance-badge">
                            <span className="paw-icon">
                              <img
                                src={pawPrintIcon}
                                alt="pawPrintIcon"
                                className="distance-paw-icon"
                                style={{ maxWidth: "none" }}
                              />
                            </span>
                            {formatDistance(dog.distance_miles)}
                          </div>
                        </div>
                      </div>

                      <div className="card-divider" />

                      <div className="card-content-info">
                        {dog.team_social !== 1 ? (
                          <>
                            <div className="info-row">
                              <img
                                src={cake}
                                alt="cake"
                                className="info-row-icon"
                                style={{ maxWidth: "none" }}
                              />{" "}
                              {dog.age_group && dog.age_group.trim() !== ""
                                ? toTitleCase(dog.age_group)
                                : getDogAgeCategory(dog.dog_birthday)}
                            </div>
                            <div className="info-row">
                              <img
                                src={pawIcon3}
                                alt="pawIcon3"
                                className="info-row-icon"
                                style={{ maxWidth: "none" }}
                              />{" "}
                              {dog.dog_breed || "Not Specified"}
                            </div>
                            <div className="info-row">
                              <img
                                src={pawIcon2}
                                alt="pawIcon2"
                                className="info-row-icon"
                                style={{ maxWidth: "16px" }}
                              />{" "}
                              {dogActivityDisplay}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="info-row">
                              <img
                                src={pawIcon2}
                                alt="experience"
                                className="info-row-icon"
                                style={{ maxWidth: "16px" }}
                              />{" "}
                              {dogActivityDisplay}
                            </div>
                          </>
                        )}
                        <div className="info-row">
                          <img
                            src={calendarIcon}
                            alt="calendarIcon"
                            className="info-row-icon"
                            style={{ maxWidth: "none" }}
                          />{" "}
                          {availabilityDisplay}
                        </div>
                      </div>

                      <div className="card-content-actions">
                        {String(dog.user_id) !== String(profile?.id) ? (
                          <>
                            <button
                              className="action-btn"
                              disabled={isTossingTreat}
                              onClick={handleTossAction}
                              onTouchEnd={handleTossAction}
                              onTouchStart={(e) => e.stopPropagation()}
                            >
                              {isTossingTreat ? (
                                <Loader2 className="animate-spin" size={12} />
                              ) : (
                                "TOSS A TREAT"
                              )}
                            </button>
                            <button
                              className="action-btn"
                              onClick={handleViewProfileAction}
                              onTouchEnd={handleViewProfileAction}
                              onTouchStart={(e) => e.stopPropagation()}
                            >
                              VIEW PROFILE
                            </button>
                          </>
                        ) : (
                          <div
                            style={{
                              textAlign: "center",
                              fontSize: "0.8rem",
                              fontWeight: "600",
                              color: "rgba(68, 138, 123, 1)",
                              padding: "5px",
                            }}
                          ></div>
                        )}
                        <button
                          className="action-btn"
                          onClick={(event) => handleShareProfile(event, dog, true)}
                          onTouchEnd={(event) => event.stopPropagation()}
                          onTouchStart={(event) => event.stopPropagation()}
                        >
                          Share Profile
                        </button>
                      </div>
                      <div className="card-arrow" />
                    </div>
                  )}
                </div>
              </OverlayViewF>
            );
          })}
        </GoogleMap>

        <button
          className="filter-btn"
          onClick={() => setIsFilterOpen(true)}
          title="Filter Map"
        >
          <img src={filterB} alt="filterIcon" className="filter-icon-img" />
        </button>
        <button
          className={`recenter-btn ${!userLocation ? "disabled" : ""}`}
          onClick={handleReCenter}
          disabled={!userLocation}
          title={
            userLocation ? "Re-center to my location" : "Location required"
          }
          style={{
            opacity: userLocation ? 1 : 0.5,
            cursor: userLocation ? "pointer" : "not-allowed",
          }}
        >
          <MapPin strokeWidth={3} className="recenter-icon-svg" />
        </button>
      </div>

      <div className="card-divider-main" />

      {/* FOOTER */}
      <div className="map-footer">
        <div className="nav-item active">
          <Dot className="nav-dot-icon" color="rgba(71, 215, 172, 1)" />
          <img src={mapIcon} alt="miniMapIcon" className="nav-icon-img" />
          <span className="nav-item-text">MAP VIEW</span>
        </div>
        <div className="nav-divider" />
        <div className="nav-item" onClick={onSwitchToList}>
          <div className="list-icon-lines">
            <Dot className="nav-dot-icon-hidden" color="" />
            <List className="nav-list-icon" style={{ marginBottom: "8px" }} />
          </div>
          <span className="nav-item-text">LIST VIEW</span>
        </div>
      </div>

      {/* FILTER PANEL */}
      <FilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApplyFilters={handleApplyFilters}
        onClear={handleClearFilters}
        profile={profile}
        userLocation={searchLocation}
        locationSource={userLocation ? "current" : "profile"}
      />

      {/* INCOMPLETE PROFILE MODAL */}
      {showIncompleteProfileModal && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div
            className="book-modal-card"
            style={{
              padding: "1.5rem",
              textAlign: "center",
              width: window.innerWidth >= 1024 ? "400px" : "",
              height: window.innerWidth >= 1024 ? "180px" : "",
            }}
          >
            <button
              className="modal-close-btn"
              onClick={() => setShowIncompleteProfileModal(false)}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <h2
              className="book-title"
              style={{
                marginTop: "1rem",
                marginBottom: "1rem",
                fontSize: "1rem",
              }}
            >
              Want to interact with the community? Complete your profile first!
              🐾
            </h2>

            <div
              style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
            >
              <button
                className="profile-btn-half"
                onClick={() => {
                  setShowIncompleteProfileModal(false);
                  rememberCompletionReturn(profile?.id, { dogData: selectedDog });
                  navigate("/team-selection", {
                    state: { routePath: "checkCompletionStatus" },
                  });
                }}
              >
                CONTINUE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TREAT MODAL */}
      {showTreatModal && treatDog && (
        <div className="treat-modal-overlay">
          <div className="treat-modal-card">
            <button
              className="treat-close-btn"
              onClick={handleCloseModal}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>
            <div className="treat-modal-content">
              <div className="treat-icons-row">
                <div
                  className="map-treat-dog-avatar"
                  style={{ position: "relative" }}
                >
                  <ImageWithPlaceholder
                    src={getAvatarData(treatDog).src}
                    placeholder={getAvatarData(treatDog).placeholder}
                    alt={treatDog.dog_name}
                    style={{ maxWidth: "none", width: "100%", height: "100%" }}
                  />
                </div>
                <img
                  src={tablerBone}
                  alt={"tablerBoneIcon"}
                  className="treat-bone-icon"
                  style={{ width: "60px" }}
                />
              </div>
              <h2 className="treat-text-main">
                You just tossed {treatDog.dog_name} a treat!
              </h2>
              <p className="treat-text-sub">
                Treats are limited to one per day per dog
              </p>
              <h3 className="treat-high-paw">HIGH PAW!</h3>
            </div>
          </div>
        </div>
      )}

      {/* BOOK WALK MODAL */}
      {showBookWalkModal && treatDog && (
        <div className="modal-overlay">
          <div className="book-modal-card">
            <button
              className="modal-close-btn"
              onClick={handleCloseModal}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>
            <div className="book-modal-header-icons">
              <div
                className="map-treat-dog-avatar"
                style={{ position: "relative" }}
              >
                <ImageWithPlaceholder
                  src={getAvatarData(treatDog).src}
                  placeholder={getAvatarData(treatDog).placeholder}
                  alt={treatDog.dog_name}
                  style={{ maxWidth: "none", width: "100%", height: "100%" }}
                />
              </div>
            </div>

            <h2 className="book-title">When would you like to walk?</h2>
            <p className="book-subtitle">
              <b style={{ color: "#000" }}>Take the first step! 🐾</b>
              <br />
              Invite {getFirstName(
                treatDog?.user_name || treatDog?.dog_name,
              )}{" "}
              for a walk. Suggest a time or keep it flexible, you can work out
              the details together once they accept.
            </p>

            <div className="book-tabs">
              <span
                className={`book-tab ${walkTab === "calendar" ? "active" : ""}`}
                onClick={() => setWalkTab("calendar")}
              >
                Calendar
              </span>
              <span
                className={`book-tab ${walkTab === "flexible" ? "active" : ""}`}
                onClick={() => setWalkTab("flexible")}
              >
                I'm Flexible
              </span>
            </div>

            {walkTab === "calendar" && (
              <div className="tab-content-calendar">
                <div className="calendar-layout">
                  <div className="custom-calendar-container">
                    <div className="cal-header-info"></div>
                    <div className="cal-selected-display">
                      <h2>
                        {value ? format(value, "EEE, MMM d") : "Select a date"}
                      </h2>
                    </div>
                    <div className="rdp-wrapper-purple">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box sx={{ width: "100%", maxWidth: 360 }}>
                          <DateCalendar
                            value={value}
                            disablePast
                            onChange={(newValue) => {
                              setValue(newValue);
                              setSelectedTime(null);
                            }}
                          />
                        </Box>
                      </LocalizationProvider>
                    </div>
                  </div>

                  <div className="time-selector-area">
                    <label>TIME</label>
                    <div
                      className="custom-dropdown"
                      style={{
                        backgroundColor: selectedTime
                          ? "#fff"
                          : "rgba(182, 252, 238, 1)",
                      }}
                      onClick={() => setShowTimeDropdown((p) => !p)}
                    >
                      <span>{selectedTime || "Choose a time"}</span>
                      <ChevronDown size={16} />
                    </div>
                    {showTimeDropdown && (
                      <div className="time-dropdown-list">
                        {TIME_SLOTS.map((time) => (
                          <div
                            key={time}
                            className={`time-option ${
                              selectedTime === time ? "active" : ""
                            }`}
                            onClick={() => {
                              setSelectedTime(time);
                              setShowTimeDropdown(false);
                            }}
                          >
                            {time}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="book-modal-next-container">
                      <button
                        className={`book-modal-next-btn ${!selectedTime ? "disabled" : ""}`}
                        disabled={!selectedTime}
                        onClick={() => {
                          const [time, meridian] = selectedTime.split(" ");
                          const [hour, minute] = time.split(":").map(Number);
                          const finalDateTime = value
                            .hour(
                              meridian === "PM" && hour !== 12
                                ? hour + 12
                                : hour === 12 && meridian === "AM"
                                  ? 0
                                  : hour,
                            )
                            .minute(minute)
                            .second(0);
                          setSelectedDate(finalDateTime);
                          openMeetingPoint();
                        }}
                      >
                        NEXT
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {walkTab === "flexible" && (
              <div className="tab-content-flexible">
                <div className="flexible-grid">
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("mornings")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("mornings")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={morningIcon}
                      alt="Mornings"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Mornings</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("afternoons")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("afternoons")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={afternoonIcon}
                      alt="Afternoon"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Afternoons</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("evenings")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("evenings")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={eveningIcon}
                      alt="Evening"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Evenings</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("weekdays")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("weekdays")}
                  >
                    <img
                      src={weekedaysIcon}
                      alt="Weekdays"
                      style={{
                        height: "40px",
                        width: "35px",
                        marginTop: "10px",
                      }}
                    />
                    <span>Weekdays</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("weekends")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("weekends")}
                    style={{ gap: "15px" }}
                  >
                    <img
                      src={weekenedsIcon}
                      alt="Weekends"
                      style={{
                        height: "35px",
                        width: "35px",
                        marginTop: "10px",
                      }}
                    />
                    <span>Weekends</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("vacation")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("vacation")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={vacationIcon}
                      alt="vacations"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Vacations</span>
                  </div>
                </div>
                <div className="book-modal-submit-container">
                  <button
                    className={`book-modal-submit-btn ${selectedFlexibleOptions.length === 0 ? "disabled" : ""}`}
                    disabled={selectedFlexibleOptions.length === 0}
                    onClick={handleFlexibleSubmit}
                  >
                    SUBMIT
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MEETING POINT MODAL */}
      {showMeetingPointModal && (
        <div className="meeting-point-overlay">
          <div className="meeting-point-card">
            <div className="meeting-point-header">
              <button className="mp-back-btn" onClick={backToBookWalk}>
                <ArrowLeft size={22} color="#000" strokeWidth={3} />
                <span style={{ color: "#000", fontWeight: "700" }}>Back</span>
              </button>
              <button
                className="mp-close-btn"
                onClick={closeAllModalsFully}
                style={{ zIndex: 999 }}
              >
                <X size={16} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
              </button>
            </div>

            <div className="meeting-point-content">
              <h2 className="mp-title">SELECT MEETING LOCATION</h2>
              <p className="mp-subtitle">
                Choose from common locations or tap on map to pin a location on
                map
              </p>

              {/* Custom Dropdown */}
              <MeetingLocationPicker
                profile={profile}
                partnerId={treatDog?.user_id}
                dogId={((treatDog?.team_social === 1 && treatDog?.team_paws !== 1) || treatDog?.user_dog_status === 0) ? profile?.dogs?.[0]?.id : treatDog?.id}
                isMapLoaded={isLoaded}
                initialCenter={userLocation}
                selectedLocation={selectedMeetingLocation}
                selectedAddress={meetingAddress}
                onSelect={(place) => {
                  setSelectedMeetingLocation(place);
                  setMeetingAddress(place.address || place.name);
                }}
              />

              <button className="mp-confirm-btn" disabled={!hasMeetingLocation(meetingAddress)} onClick={openWalkConfirm}>
                NEXT: PERSONALIZE YOUR OFFER
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WALK CONFIRM MODAL (Message) */}
      {showWalkConfirmModal && (
        <div className="walk-confirm-overlay">
          <div className="walk-confirm-card">
            <div className="walk-confirm-header">
              <button className="wc-back-btn" onClick={backToMeetingPoint}>
                <ArrowLeft size={22} color="#000" strokeWidth={3} />
                <span style={{ color: "#000", fontWeight: "700" }}>Back</span>
              </button>
              <button
                className="wc-close-btn"
                onClick={closeAllModalsFully}
                style={{ zIndex: 999 }}
              >
                <X size={16} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
              </button>
            </div>
            <div className="walk-confirm-content">
              <h2 className="wc-title">
                You would like to walk with {treatDog?.dog_name} &{" "}
                {treatDog?.user_name}{" "}
                {selectedDate ? (
                  `on ${dayjs(selectedDate).format("ddd, MMM D [at] h:mma")}`
                ) : (
                  <>
                    with <span>{selectedFlexibleOptions.join(", ")}</span>{" "}
                  </>
                )}
              </h2>

              <p className="wc-message-label">
                Personalize your offer with a message
              </p>
              <textarea
                className="wc-message-input"
                placeholder="Write here"
                value={walkMessage}
                onChange={(e) => setWalkMessage(e.target.value)}
              />

              <div className="wc-actions-row">
                <button
                  className="wc-btn confirm"
                  onClick={handleCreateWalk}
                  disabled={isCreatingWalk}
                >
                  {isCreatingWalk ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    "NEXT: CONFIRM YOUR OFFER BEFORE SENDING"
                  )}
                </button>
              </div>
              <p className="wc-footer-text">
                We will reach out to {treatDog?.user_name} with your suggested
                time and find the best time for your first group walk!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal-card">
            <button
              className="modal-close-btn"
              onClick={closeAllModalsFully}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>
            <h2 className="success-title">
              Your request to{" "}
              {getFirstName(treatDog?.user_name || treatDog?.dog_name)} is sent
            </h2>

            {walkMessage && (
              <div className="success-msg-preview">
                <p style={{ margin: "0 0 4px 0", fontWeight: "600" }}>
                  Your message:
                </p>
                <p style={{ margin: 0, fontStyle: "italic" }}>
                  "{walkMessage}"
                </p>
              </div>
            )}

            <h3 className="success-mp-title">Your Meeting point will be:</h3>
            <div className="success-map-box">
              <div className="success-map-placeholder">
                <div className="group-walk-pill">
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "#448a7b",
                      }}
                    ></div>{" "}
                    GROUP WALK
                  </span>
                </div>
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={selectedMeetingLocation || userLocation}
                    zoom={15}
                    options={{
                      disableDefaultUI: true,
                      gestureHandling: "none",
                    }}
                  >
                    <MarkerF
                      position={selectedMeetingLocation || userLocation}
                    />
                  </GoogleMap>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#e3f2fd",
                    }}
                  />
                )}
              </div>
              <div className="success-mp-details">
                <h4>
                  {meetingAddress
                    ? meetingAddress.split(",")[0]
                    : "Selected Location"}
                </h4>
                <p>
                  <MapPin size={14} color="#009bce" /> {meetingAddress}
                </p>
              </div>
              <div className="success-chips">
                <div className="success-chip blue">
                  <span>{getDynamicDateLabel(selectedDate)}</span>
                  <strong style={{ color: "#000" }}>
                    {selectedDate
                      ? dayjs(selectedDate).format("ddd, MMM D")
                      : selectedFlexibleOptions.join(", ")}
                  </strong>
                </div>
                {selectedDate && (
                  <div className="success-chip green">
                    <span>{getDynamicTimeLabel(selectedDate)}</span>
                    <strong style={{ color: "#000" }}>
                      {dayjs(selectedDate).format("h:mm A")}
                    </strong>
                  </div>
                )}
              </div>
            </div>
            <button className="success-done-btn" onClick={closeAllModalsFully}>
              DONE
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapDashboardPage;
